/**
 * /pressure-testing/alerts — instruments that need a calibration
 * (fieldiq-api `GET /pressure-testing/calibrations/alerts`).
 *
 * The list, its order and each row's status are the API's. Since api#915 the
 * list includes NEVER-CALIBRATED instruments — the test guard refuses them, so
 * they need a calibration as much as an overdue one does. They are drawn as
 * their own group, below the overdue / due-soon group, because "no record" is
 * a gap in the data rather than a lapse: grouping is by the API's
 * `calibration_status`, never re-derived. A retired instrument never alerts.
 *
 * The counts are a tally of the API's alert list, which is unpaginated and is
 * the whole answer; no endpoint returns them.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Clock, HelpCircle, Loader2 } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { usePtCalibrationAlerts } from '../../hooks/usePressureTesting';
import { formatCalendarDate } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { formatPressureRange, ptKindLabel, ptStatusSkin } from '../../lib/pressure-testing';
import type { PtInstrument, PtPressureUnit } from '../../types/pressure-testing';
import { dueHint } from './InstrumentsScreen';
import { PtEmpty, PtPanel, PtStatusBadge } from './PtBits';

const day = (value: string | null | undefined) => formatCalendarDate(value) ?? '—';

export default function CalibrationAlertsScreen() {
  const navigate = useNavigate();
  const query = usePtCalibrationAlerts();
  const rows = query.data?.data ?? [];
  const unit = query.data?.meta.pressure_unit ?? null;
  const failure = queryLoadFailure(query);
  const loaded = !query.isLoading && !failure;
  const count = (status: string) => (loaded ? rows.filter((r) => r.calibration_status === status).length : undefined);
  const warnWindow = query.data?.meta.cal_warn_days?.length ? Math.max(...query.data.meta.cal_warn_days) : null;

  const lapsing = rows.filter((r) => r.calibration_status !== 'never');
  const never = rows.filter((r) => r.calibration_status === 'never');

  const open = (id: string) => navigate(`/pressure-testing/instruments/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };

  return (
    <div data-testid="pt-alerts-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure testing"
        title="Calibration Alerts"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Overdue',
            sub: 'Past due',
            tone: 'accent',
            loading: query.isLoading,
            value: displayValue(count('overdue')),
          },
          {
            icon: <Clock className="size-4" aria-hidden="true" />,
            label: 'Due soon',
            sub: warnWindow ? `Within ${warnWindow} days` : undefined,
            tone: 'accent',
            loading: query.isLoading,
            value: displayValue(count('due_soon')),
          },
          {
            icon: <HelpCircle className="size-4" aria-hidden="true" />,
            label: 'Never',
            sub: 'No record',
            tone: 'neutral',
            loading: query.isLoading,
            value: displayValue(count('never')),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PtPanel eyebrow="Needs calibration" title="Overdue and due soon" testId="pt-alert-list">
            {query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading alerts" />
              </div>
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="calibration alerts" />
            ) : lapsing.length === 0 ? (
              <PtEmpty>No instrument is overdue or due soon.</PtEmpty>
            ) : (
              <AlertRows rows={lapsing} unit={unit} open={open} rowKeys={rowKeys} />
            )}
          </PtPanel>

          {/* Shown only once the list arrived: a failed read is said once, above, not twice. */}
          {loaded && (
            <PtPanel eyebrow="Needs calibration" title="Never calibrated" testId="pt-never-list">
              {never.length === 0 ? (
                <PtEmpty>Every instrument has a calibration on record.</PtEmpty>
              ) : (
                <AlertRows rows={never} unit={unit} open={open} rowKeys={rowKeys} />
              )}
            </PtPanel>
          )}
        </div>
      </section>
    </div>
  );
}

function AlertRows({
  rows,
  unit,
  open,
  rowKeys,
}: {
  rows: PtInstrument[];
  unit: PtPressureUnit | null;
  open: (id: string) => void;
  rowKeys: (id: string) => (event: React.KeyboardEvent) => void;
}) {
  return (
    <ul className="divide-y divide-fieldiq-border">
      {rows.map((i) => (
        <li
          key={i.id}
          role="button"
          tabIndex={0}
          onClick={() => open(i.id)}
          onKeyDown={rowKeys(i.id)}
          data-testid="pt-alert-row"
          className={`flex cursor-pointer items-center gap-4 border-l-4 px-4 py-3 outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${
            ptStatusSkin(i.calibration_status).rail
          }`}
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-extrabold text-white">{i.name || '—'}</p>
            <p className="mt-0.5 text-xs text-fieldiq-muted">
              <span className="font-mono">{i.serial_number || '—'}</span>
              {' · '}
              {ptKindLabel(i.specs?.instrument_kind)}
              {' · '}
              {formatPressureRange(i.specs?.range_min, i.specs?.range_max, unit)}
            </p>
          </div>
          <div className="hidden text-right sm:block">
            <p className="text-sm text-fieldiq-text">Due {day(i.latest_calibration?.due_on)}</p>
            <p className="text-xs text-fieldiq-muted">{dueHint(i.days_until_due) ?? '—'}</p>
          </div>
          <PtStatusBadge status={i.calibration_status} />
          <ChevronRight className="size-4 shrink-0 text-fieldiq-muted" aria-hidden="true" />
        </li>
      ))}
    </ul>
  );
}
